import { Popover, PopoverContent, PopoverFooter, PopoverHeader } from '@/components/ui/Popover';
import { useState } from 'react';

// ============================================================================
// TIPOS
// ============================================================================

export interface Column {
  key: string;
  label: string;
}

export interface ColumnPickerProps {
  /** Lista de columnas disponibles */
  columns: readonly Column[];
  /** Columnas actualmente seleccionadas (keys) */
  selectedColumns: string[];
  /** Callback cuando cambia la selección */
  onSelectionChange: (selected: string[]) => void;
  /** Color del tema (para badges y checkboxes) */
  themeColor?: 'blue' | 'red' | 'emerald';
  /** Etiqueta del botón */
  label?: string;
  /** Deshabilitar el picker */
  disabled?: boolean;
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function ColumnPicker({
  columns,
  selectedColumns,
  onSelectionChange,
  themeColor = 'blue',
  label = 'Columnas',
  disabled = false,
}: ColumnPickerProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Colores según tema
  const colorClasses = {
    blue: {
      badge: 'bg-blue-100 text-blue-700',
      checkbox: 'text-blue-600 focus:ring-blue-500',
      selected: 'bg-blue-50 border-blue-300 text-blue-700',
      button: 'text-blue-600 hover:text-blue-800',
    },
    red: {
      badge: 'bg-red-100 text-red-700',
      checkbox: 'text-red-600 focus:ring-red-500',
      selected: 'bg-red-50 border-red-300 text-red-700',
      button: 'text-red-600 hover:text-red-800',
    },
    emerald: {
      badge: 'bg-emerald-100 text-emerald-700',
      checkbox: 'text-emerald-600 focus:ring-emerald-500',
      selected: 'bg-emerald-50 border-emerald-300 text-emerald-700',
      button: 'text-emerald-600 hover:text-emerald-800',
    },
  }[themeColor];

  const toggleColumn = (key: string) => {
    if (selectedColumns.includes(key)) {
      onSelectionChange(selectedColumns.filter((k) => k !== key));
    } else {
      onSelectionChange([...selectedColumns, key]);
    }
  };

  const selectAll = () => {
    onSelectionChange(columns.map((c) => c.key));
  };

  const selectNone = () => {
    onSelectionChange([]);
  };

  const allSelected = selectedColumns.length === columns.length;
  const noneSelected = selectedColumns.length === 0;

  return (
    <Popover
      open={isOpen}
      onOpenChange={setIsOpen}
      disabled={disabled}
      align="end"
      trigger={
        <button
          type="button"
          disabled={disabled}
          className={`
            inline-flex items-center gap-2 px-3 py-2
            bg-white border border-gray-300 rounded-lg
            text-sm font-medium text-gray-700
            hover:bg-gray-50 hover:border-gray-400
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
            disabled:opacity-50 disabled:cursor-not-allowed
            transition-colors
          `}
        >
          {/* Icono de ojo */}
          <svg
            className="w-4 h-4 text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
            />
          </svg>
          <span className="hidden sm:inline">{label}</span>
          {/* Badge con contador */}
          <span
            className={`
              px-1.5 py-0.5 text-xs font-semibold rounded-full
              ${colorClasses.badge}
            `}
          >
            {selectedColumns.length}
          </span>
          {/* Chevron */}
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      }
    >
      <div className="w-80 max-h-[70vh] flex flex-col">
        {/* Header */}
        <PopoverHeader onClose={() => setIsOpen(false)}>
          <div className="flex items-center gap-2">
            <svg
              className="w-4 h-4 text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            Campos del Reporte
          </div>
        </PopoverHeader>

        {/* Acciones rápidas */}
        <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
          <span className="text-xs text-gray-500">
            {selectedColumns.length} de {columns.length} seleccionados
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={selectAll}
              disabled={allSelected}
              className={`
                text-xs font-medium
                ${allSelected ? 'text-gray-400 cursor-not-allowed' : colorClasses.button}
              `}
            >
              Todos
            </button>
            <span className="text-gray-300">|</span>
            <button
              type="button"
              onClick={selectNone}
              disabled={noneSelected}
              className={`
                text-xs font-medium
                ${noneSelected ? 'text-gray-400 cursor-not-allowed' : 'text-gray-500 hover:text-gray-700'}
              `}
            >
              Ninguno
            </button>
          </div>
        </div>

        {/* Lista de columnas en 2 columnas */}
        <PopoverContent className="overflow-y-auto max-h-64">
          <div className="grid grid-cols-2 gap-2">
            {columns.map((column) => {
              const isSelected = selectedColumns.includes(column.key);
              return (
                <label
                  key={column.key}
                  className={`
                    flex items-center gap-2 p-2 rounded-lg border cursor-pointer
                    transition-all text-xs
                    ${
                      isSelected
                        ? colorClasses.selected
                        : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                    }
                  `}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleColumn(column.key)}
                    className={`
                      w-3.5 h-3.5 rounded border-gray-300
                      ${colorClasses.checkbox}
                    `}
                  />
                  <span className="truncate" title={column.label}>
                    {column.label}
                  </span>
                </label>
              );
            })}
          </div>
        </PopoverContent>

        {/* Footer con advertencia si hay muchas columnas */}
        {selectedColumns.length > 8 && (
          <PopoverFooter>
            <div className="flex items-start gap-2 text-xs text-amber-700">
              <svg
                className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <span>Muchos campos seleccionados. Usa orientación horizontal para el PDF.</span>
            </div>
          </PopoverFooter>
        )}
      </div>
    </Popover>
  );
}

export default ColumnPicker;
